import React, { useCallback } from 'react';
import { useQuestionsStore } from '../../../utils/questionStore';
import { useStepsStore } from '../../../utils/stepStore';
import { parseQuestionSteps } from '../../../utils/parseSteps';
import { hebrewLetters } from '../../../configs/config';
import { showErrorToast, showInfoToast } from '../../../components/Toaster';
import { ExerciseQuestionsService } from '../../../services/exercise-questions/exercise-questions.service';
import { ChatService } from '../../../services/chat/chat.services';
import { Message } from '../useExercisePractice';
import { getNotebookPromptOptions } from './exercisePracticeResume';

const exerciseQuestionsService = new ExerciseQuestionsService();
const chatService = new ChatService();

const getFormattedSolutionTitle = (t: any, letter: string): string => {
    if (!letter) return "";
    return `**${t("sectionSolution")} ${letter}**\n\n`;
};

export interface SocraticFlowParams {
  isFromLessonList?: boolean;
  exerciseId: number | null;
  exerciseIdRef: React.MutableRefObject<number | null>;
  selectedQuestionId: number | null;
  selectedQuestionIdRef: React.MutableRefObject<number | null>;
  setSelectedQuestionId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedQuestion: any | null;
  setSelectedQuestion: React.Dispatch<React.SetStateAction<any | null>>;
  selectedQuestionRef: React.MutableRefObject<any | null>;
  selectedQuestionLetter: string | null;
  selectedQuestionLetterRef: React.MutableRefObject<string | null>;
  setSelectedQuestionLetter: React.Dispatch<React.SetStateAction<string | null>>;
  activeQuestionSteps: any[];
  activeQuestionStepsRef: React.MutableRefObject<any[]>;
  setActiveQuestionSteps: React.Dispatch<React.SetStateAction<any[]>>;
  activeStepIndex: number;
  activeStepIndexRef: React.MutableRefObject<number>;
  setActiveStepIndex: React.Dispatch<React.SetStateAction<number>>;
  allStepsCompleted: boolean;
  allStepsCompletedRef: React.MutableRefObject<boolean>;
  setAllStepsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  showOptionsForStep: boolean;
  setShowOptionsForStep: React.Dispatch<React.SetStateAction<boolean>>;
  exerciseQuestions: any[];
  exerciseQuestionsRef: React.MutableRefObject<any[]>;
  setExerciseQuestions: React.Dispatch<React.SetStateAction<any[]>>;
  isTyping: boolean;
  setIsTyping: React.Dispatch<React.SetStateAction<boolean>>;
  messages: Message[];
  messagesRef: React.MutableRefObject<Message[]>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  addBotMessage: (msg: Partial<Message>, delay?: number) => Promise<string>;
  waitForMessageCompletion: (id: string) => Promise<void>;
  studentName: string;
  user_attributes: any;
  t: (key: string, options?: any) => string;
  handleCaptureStart?: () => void;
  handleCaptureStop?: () => void;
}

export const useSocraticFlow = (params: SocraticFlowParams) => {
  const {
    isFromLessonList,
    handleCaptureStart,
    handleCaptureStop,
    exerciseId,
    exerciseIdRef,
    selectedQuestionId,
    selectedQuestionIdRef,
    setSelectedQuestionId,
    selectedQuestion,
    setSelectedQuestion,
    selectedQuestionRef,
    selectedQuestionLetter,
    selectedQuestionLetterRef,
    setSelectedQuestionLetter,
    activeQuestionSteps,
    activeQuestionStepsRef,
    setActiveQuestionSteps,
    activeStepIndex,
    activeStepIndexRef,
    setActiveStepIndex,
    allStepsCompleted,
    allStepsCompletedRef,
    setAllStepsCompleted,
    showOptionsForStep,
    setShowOptionsForStep,
    exerciseQuestions,
    exerciseQuestionsRef,
    setExerciseQuestions,
    isTyping,
    setIsTyping,
    messages,
    messagesRef,
    setMessages,
    addBotMessage,
    waitForMessageCompletion,
    studentName,
    user_attributes,
    t
  } = params;

  /**
   * Initiates loading question steps, displaying the loader bubble immediately and fetching steps from the server
   */
  const loadQuestionSteps = useCallback(async (retry: boolean = false, targetQId?: number, targetQ?: any) => {
    const qId = targetQId || selectedQuestionIdRef.current;
    const q = targetQ || selectedQuestionRef.current;
    if (!exerciseId || !qId || !q) return;

    if (retry) {
      setMessages(prev => prev.filter(m => m.type !== 'error_bubble'));
    }

    const introMsgId = `steps-intro-${qId}`;
    setMessages(prev => {
      if (prev.some(m => m.id === introMsgId)) return prev;
      return [
        ...prev,
        {
          id: introMsgId,
          sender: 'bot',
          text: '',
          type: 'loader_bubble',
          loadingText: t('exercisePracticeExplainStepsIntro')
        }
      ];
    });

    try {
      setIsTyping(true);
      const currentSelectedQuestion = q || selectedQuestionRef.current;
      const payload = {
        exercise_id: exerciseId,
        selected_question: currentSelectedQuestion.question,
        selected_question_id: qId
      };

      const response = await chatService.GETQUESTIONSTEPS(payload);
      if (!response || !response.response_content) {
        throw new Error("No steps content returned");
      }

      const qLetter = selectedQuestionLetterRef.current || 'א';
      const parsedSteps = parseQuestionSteps(response.response_content);
      if (parsedSteps.length > 0) {
        parsedSteps[0].title = `${t("importantGuidelinesForSolving")} ${qLetter}`;
      }
      setActiveQuestionSteps(parsedSteps);
      setActiveStepIndex(0);
      setAllStepsCompleted(false);
      setShowOptionsForStep(false);
      useQuestionsStore.getState().setSteps(qId, parsedSteps);

      // Remove loader bubble and append steps list card in a single state update
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== introMsgId);
        const listMsgId = `steps-list-bubble-${qId}`;
        if (filtered.some(m => m.id === listMsgId)) return filtered;
        return [
          ...filtered,
          {
            id: listMsgId,
            sender: 'bot',
            text: '',
            type: 'steps_list',
            steps: parsedSteps,
            questionId: qId,
            questionLetter: qLetter
          }
        ];
      });

    } catch (error) {
      console.error("Failed to load question steps:", error);
      setMessages(prev => prev.filter(m => m.id !== introMsgId));
      setMessages(prev => [
        ...prev,
        {
          id: 'steps-error',
          sender: 'bot',
          text: t("exercisePracticeConnectionError"),
          type: 'error_bubble',
          errorContext: 'steps'
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  }, [exerciseId, selectedQuestionId, selectedQuestion, selectedQuestionLetter, setMessages, setIsTyping, setActiveQuestionSteps, setActiveStepIndex, setAllStepsCompleted, setShowOptionsForStep, selectedQuestionRef, t]);

  /**
   * Updates state when a question is selected in the questions list bubble card
   */
  const selectQuestionFromBubble = useCallback((question: any, letter: string) => {
    handleCaptureStop?.();
    setSelectedQuestionId(question.id);
    setSelectedQuestion(question);
    setSelectedQuestionLetter(letter);
    useQuestionsStore.getState().setCurrentSelectedQuestion(question.id);
    useQuestionsStore.getState().setSelectedQuestionLetter(letter);
    if (question.chart_image_data) {
      useQuestionsStore.getState().setChart({
        question_id: question.id,
        chart_image_base64: question.chart_image_data,
      });
    }
    // Filter out options from the select-section-prompt messages
    setMessages(prev => prev.map(m => {
      if (m.id === 'select-section-prompt' || m.id?.startsWith('select-section-prompt-resume-')) {
        const { options, ...rest } = m;
        return rest;
      }
      return m;
    }).filter(m => !m.id?.startsWith('select-question-options-')));

    // Immediately load the steps (Requirement 2)
    loadQuestionSteps(false, question.id, question);
  }, [setSelectedQuestionId, setSelectedQuestion, setSelectedQuestionLetter, setMessages, loadQuestionSteps, handleCaptureStop]);

  /**
   * Action when the student successfully solves a step in their notebook
   */
  const handleStepSolved = useCallback(async (index: number, stepsList: any[], qId: number, qLetter: string) => {
    const currentExerciseId = exerciseIdRef.current;
    const resolvedStepId = stepsList[index]?.id;
    if (currentExerciseId && qId && resolvedStepId) {
      exerciseQuestionsService.upsertExerciseHistory({
        selected_exercise_id: currentExerciseId,
        selected_question_id: String(qId),
        selected_step_id: resolvedStepId,
        is_completed_steps: true,
        is_completed_question: index + 1 >= stepsList.length
      }).catch(e => {
        console.error("Upsert history failed in background:", e);
      });
      useQuestionsStore.getState().markQuestionInProcess(currentExerciseId, qId);
      useStepsStore.getState().addCompletedStep(qId, resolvedStepId);
    }

    // Check if we finished all steps for this question
    const isCompleted = index + 1 >= stepsList.length;
    if (isCompleted) {
      if (qId) {
        useQuestionsStore.getState().addCompletedQuestion(exerciseId, qId);
      }

      // Update question completion status in local state list
      setExerciseQuestions(prev => prev.map(q => q.id === qId ? { ...q, isCompleted: true } : q));

      // Display the drawing chart if it exists for this question and hasn't been shown yet
      const chart = useQuestionsStore.getState().getChartByQuestionId(qId);
      const isChartAlreadyShown = messagesRef.current.some(m => m.type === 'chart' && m.id?.startsWith(`chart-${qId}`));
      if (chart && !isChartAlreadyShown) {
        const chartIntroId = `chart-intro-${qId}-${Date.now()}`;
        await addBotMessage({
          id: chartIntroId,
          text: t("scketchSolution"),
          type: 'text'
        });
        await waitForMessageCompletion(chartIntroId);

        const chartMsgId = `chart-${qId}-${Date.now()}`;
        await addBotMessage({
          id: chartMsgId,
          text: `<img src="${chart}" alt="chart" class="chat-chart-image" />`,
          type: 'chart'
        });
        await waitForMessageCompletion(chartMsgId);
      }

      const genderNum = user_attributes?.gender;
      const genderStr = genderNum === 2 ? 'female' : 'male';

      // Find the next incomplete question
      const updatedQuestions = exerciseQuestionsRef.current.map(q => q.id === qId ? { ...q, isCompleted: true } : q);
      const nextQ = updatedQuestions.find(q => !q.isCompleted);
      const nextQIndex = updatedQuestions.findIndex(q => !q.isCompleted);

      const promptText = nextQ
        ? t(`exercisePracticeQuestionCompleteCelebration_${genderStr}`, { studentName, questionLetter: qLetter })
        : isFromLessonList
          ? t(`exercisePracticeExerciseCompleteInLesson_${genderStr}`, { studentName })
          : t(`exercisePracticeExerciseCompleteCelebration_${genderStr}`, { studentName });

      const celebrationMsgId = `question-celebration-${qId}-${Date.now()}`;

      // Display the last state of the question list card only if there are remaining questions
      if (nextQ) {
        const listBubbleId = `questions-list-bubble-celebration-${Date.now()}`;
        setMessages(prev => [
          ...prev,
          {
            id: listBubbleId,
            sender: 'bot',
            text: '',
            type: 'questions_list',
            questions: updatedQuestions
          }
        ]);
      }

      // Display 20 ms typing mode bubble message
      await addBotMessage({
        id: celebrationMsgId,
        text: promptText,
        type: 'text'
      }, 20);

      // Wait for the celebration text to finish typing
      await waitForMessageCompletion(celebrationMsgId);

    } else {
      // Transition to display the next step inline
      displayStepInline(index + 1, stepsList, qId, qLetter);
    }
  }, [
    exerciseId,
    exerciseQuestions,
    user_attributes,
    studentName,
    loadQuestionSteps,
    t,
    setExerciseQuestions,
    setSelectedQuestionId,
    setSelectedQuestion,
    setSelectedQuestionLetter,
    selectedQuestionRef,
    setMessages,
    exerciseIdRef,
    addBotMessage,
    waitForMessageCompletion
  ]);

  /**
   * Triggers a deep Socratic explanation when student requests further elaboration
   */
  const handleExplainMore = useCallback(async (index: number, stepsList: any[], prevResponse: string, qId: number, qLetter: string) => {
    setIsTyping(true);

    try {
      const currentSelectedQuestion = selectedQuestionRef.current;
      const currentExerciseId = exerciseIdRef.current;

      const requestData = {
        exercise_id: currentExerciseId,
        prev_response: prevResponse,
        selected_question: currentSelectedQuestion?.question,
        selected_question_id: qId ? Number(qId) : null,
        selected_step: `<b>${t("STEP")} ${index + 1}: ${stepsList[index].title}</b> ${stepsList[index].content}`,
        all_steps_solution: false,
        is_details_explain: true,
        chat_history: []
      };

      const response = await chatService.EXPLAINSTEPSOLUTION(requestData);
      if (response && response.solution_content) {
        const deepExplanationMsgId = `deep-explanation-${stepsList[index].id}`;
        await addBotMessage({
          id: deepExplanationMsgId,
          text: response.solution_content,
          type: 'text'
        });

        // Wait for deep explanation to finish typing
        await waitForMessageCompletion(deepExplanationMsgId);

        // Let the user progress to next step
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const readyText = t(`exercisePracticeReadyToProceed_${genderStr}`, { studentName });

        await addBotMessage({
          id: `deep-explanation-next-${stepsList[index].id}`,
          text: readyText,
          type: 'options',
          options: [
            {
              label: t("understoodNextStep"),
              value: "next_step",
              action: () => handleStepSolved(index, stepsList, qId, qLetter)
            }
          ]
        });
      }
    } catch (e) {
      console.error("Explain more failed:", e);
      showErrorToast(t("communicationError"));
    } finally {
      setIsTyping(false);
    }
  }, [handleStepSolved, addBotMessage, waitForMessageCompletion, user_attributes, studentName, t, setIsTyping, selectedQuestionRef, exerciseIdRef]);

  /**
   * Fetches detailed solution content for a specific step and replaces the loader bubble
   */
  const handleShowStepSolution = useCallback(async (index: number, stepsList: any[], qId: number, qLetter: string) => {
    handleCaptureStop?.();
    const solverLoaderId = `solver-loader-${stepsList[index].id}-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: solverLoaderId,
        sender: 'bot',
        text: '',
        type: 'loader_bubble',
        loadingText: t("exercisePracticeSolvingAndDisplaying")
      }
    ]);

    try {
      const currentSelectedQuestion = selectedQuestionRef.current;
      const currentExerciseId = exerciseIdRef.current;
      const currentMessages = messagesRef.current;

      if (!currentSelectedQuestion) {
        throw new Error("No question selected");
      }

      const stepContent = `<b>${t("STEP")} ${index + 1}: ${stepsList[index].title}</b> ${stepsList[index].content}`;
      const requestPayload: any = {
        exercise_id: currentExerciseId,
        solution_steps: stepContent,
        selected_question: currentSelectedQuestion.question,
        selected_question_id: qId,
        selected_step_id: stepsList[index].id,
        all_steps_solution: false,
        chat_history: currentMessages.slice(-10).map(m => ({
          role: m.sender === 'bot' ? 'assistant' : 'user',
          content: m.text
        }))
      };
      if (index === 0) {
        requestPayload.is_first_step = true;
      }

      const response = await chatService.SOLUTIONBYSTEPS(requestPayload);
      if (response && response.solution_content) {
        // Update history
        const resolvedStepId = stepsList[index]?.id;
        if (currentExerciseId && qId && resolvedStepId) {
          exerciseQuestionsService.upsertExerciseHistory({
            selected_exercise_id: currentExerciseId,
            selected_question_id: String(qId),
            selected_step_id: resolvedStepId,
            is_completed_steps: true,
            is_completed_question: index + 1 >= stepsList.length
          }).catch(e => {
            console.error("Upsert history failed in background:", e);
          });
          useQuestionsStore.getState().markQuestionInProcess(currentExerciseId, qId);
          useStepsStore.getState().addCompletedStep(qId, resolvedStepId);
        }

        const title = getFormattedSolutionTitle(t, qLetter);
        const formattedSolution = `${title}${response.solution_content}`;

        // Replace loader with the solution content
        setMessages(prev => prev.map(m => m.id === solverLoaderId ? {
          id: solverLoaderId,
          sender: 'bot',
          text: formattedSolution,
          type: 'text'
        } : m));

        // Wait for the solution text to finish typing
        await waitForMessageCompletion(solverLoaderId);

        // Display the drawing chart if this is the final step of the question, it exists, and hasn't been shown yet
        const isFinalStep = index + 1 >= stepsList.length;
        if (isFinalStep) {
          const chart = useQuestionsStore.getState().getChartByQuestionId(qId);
          const isChartAlreadyShown = messagesRef.current.some(m => m.type === 'chart' && m.id?.startsWith(`chart-${qId}`));
          if (chart && !isChartAlreadyShown) {
            const chartIntroId = `chart-intro-${qId}-${Date.now()}`;
            await addBotMessage({
              id: chartIntroId,
              text: t("scketchSolution"),
              type: 'text'
            });
            await waitForMessageCompletion(chartIntroId);

            const chartMsgId = `chart-${qId}-${Date.now()}`;
            await addBotMessage({
              id: chartMsgId,
              text: `<img src="${chart}" alt="chart" class="chat-chart-image" />`,
              type: 'chart'
            });
            await waitForMessageCompletion(chartMsgId);
          }
        }

        // Follow up options
        const genderNum = user_attributes?.gender;
        const genderStr = genderNum === 2 ? 'female' : 'male';
        const furtherExplainText = t(`exercisePracticeFurtherExplanation_${genderStr}`, { studentName });

        await addBotMessage({
          id: `solution-followup-${stepsList[index].id}-${Date.now()}`,
          text: furtherExplainText,
          type: 'options',
          options: [
            {
              label: t("exercisePracticeYes"),
              value: "explain_more",
              action: () => handleExplainMore(index, stepsList, response.solution_content, qId, qLetter)
            },
            {
              label: t("understoodNextStep"),
              value: "next_step",
              action: () => handleStepSolved(index, stepsList, qId, qLetter)
            }
          ]
        });
      } else {
        throw new Error("No solution content received");
      }
    } catch (error) {
      console.error("Failed to load step solution:", error);
      // Remove loader bubble in case of failure
      setMessages(prev => prev.filter(m => m.id !== solverLoaderId));
      showErrorToast(t("communicationError"));
    }
  }, [
    handleExplainMore,
    handleStepSolved,
    waitForMessageCompletion,
    addBotMessage,
    user_attributes,
    studentName,
    t,
    setMessages,
    selectedQuestionRef,
    exerciseIdRef,
    messagesRef,
    handleCaptureStop
  ]);

  /**
   * Socratic tutoring help when student clicks "not quite there yet" for a step
   */
  const handleStepTutoring = useCallback(async (index: number, stepsList: any[], qId: number, qLetter: string) => {
    handleCaptureStop?.();
    const tutorLoaderId = `tutor-loader-${stepsList[index].id}-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: tutorLoaderId,
        sender: 'bot',
        text: '',
        type: 'loader_bubble',
        loadingText: t("exercisePracticeGuidingAndDisplaying")
      }
    ]);

    try {
      const currentSelectedQuestion = selectedQuestionRef.current;
      const currentExerciseId = exerciseIdRef.current;
      const currentMessages = messagesRef.current;

      const stepContent = `<b>${t("STEP")} ${index + 1}: ${stepsList[index].title}</b> ${stepsList[index].content}`;
      const requestPayload: any = {
        exercise_id: currentExerciseId,
        prev_response: null,
        selected_question: currentSelectedQuestion?.question,
        selected_question_id: qId ? Number(qId) : null,
        selected_step: stepContent,
        all_steps_solution: false,
        is_details_explain: false,
        chat_history: currentMessages.slice(-10).map(m => ({
          role: m.sender === 'bot' ? 'assistant' : 'user',
          content: m.text
        }))
      };
      if (index === 0) {
        requestPayload.is_first_step = true;
      }

      const response = await chatService.EXPLAINSTEPSOLUTION(requestPayload);
      if (response && response.solution_content) {
        const formattedHint = qLetter 
            ? `**${t("hintForSection")} ${qLetter}**\n\n${response.solution_content}`
            : response.solution_content;

        // Replace loader with explanation content and options
        setMessages(prev => prev.map(m => m.id === tutorLoaderId ? {
          id: tutorLoaderId,
          sender: 'bot',
          text: formattedHint,
          type: 'options',
          optionsClassName: 'solution-capture-options-layout',
          options: getNotebookPromptOptions(
            t,
            () => handleShowStepSolution(index, stepsList, qId, qLetter),
            () => handleStepTutoring(index, stepsList, qId, qLetter),
            showInfoToast,
            handleCaptureStart,
            () => handleStepSolved(index, stepsList, qId, qLetter)
          )
        } : m));

        // Wait for tutor explanation to finish typing
        await waitForMessageCompletion(tutorLoaderId);
      } else {
        throw new Error("No explanation content received");
      }
    } catch (error) {
      console.error("Tutor request failed:", error);
      setMessages(prev => prev.filter(m => m.id !== tutorLoaderId));
      showErrorToast(t("communicationError"));
    }
  }, [
    handleStepSolved,
    handleShowStepSolution,
    addBotMessage,
    waitForMessageCompletion,
    user_attributes,
    t,
    setMessages,
    selectedQuestionRef,
    exerciseIdRef,
    messagesRef,
    handleCaptureStop
  ]);

  /**
   * Renders a specific question step content inline, prompting the student for notebook solving
   */
  const displayStepInline = useCallback(async (index: number, stepsList: any[], qId: number, qLetter: string) => {
    const step = stepsList[index];
    if (!step) return;

    setActiveStepIndex(index);

    // Prompt: "Have you solved this step in your notebook?"
    const genderNum = user_attributes?.gender;
    const genderStr = genderNum === 2 ? 'female' : 'male';
    const notebookPromptText = t(`stepSolveInNotebook_${genderStr}`, { studentName });

    await addBotMessage({
      id: `step-notebook-prompt-${step.id}-${index}`,
      text: notebookPromptText,
      type: 'options',
      optionsClassName: 'solution-capture-options-layout',
      options: getNotebookPromptOptions(
        t,
        () => handleShowStepSolution(index, stepsList, qId, qLetter),
        () => handleStepTutoring(index, stepsList, qId, qLetter),
        showInfoToast,
        handleCaptureStart,
        () => handleStepSolved(index, stepsList, qId, qLetter)
      )
    });
  }, [addBotMessage, user_attributes, studentName, t, handleShowStepSolution, handleStepTutoring, setActiveStepIndex, handleCaptureStart]);

  return {
    selectQuestionFromBubble,
    loadQuestionSteps,
    displayStepInline,
    handleStepSolved,
    handleExplainMore,
    handleShowStepSolution,
    handleStepTutoring
  };
};
